import React, { useState, useCallback, useRef } from 'react';
import ReactCrop, { Crop, PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

interface CropInterfaceProps {
  image: File;
  onCrop: (croppedImage: string) => void;
  onBack: () => void;
  initialAspectRatio?: number;
}

interface AspectRatioOption {
  label: string;
  value: number;
  icon?: string;
  description?: string;
}

const aspectRatioOptions: AspectRatioOption[] = [
  { label: 'Free', value: 0, icon: '⚡', description: 'No constraints' },
  { label: 'Square', value: 1, icon: '⬛', description: 'Perfect for profile pictures (1:1)' },
  { label: 'Portrait', value: 3/4, icon: '📱', description: 'Mobile-friendly vertical (3:4)' },
  { label: 'Landscape', value: 16/9, icon: '🖥️', description: 'Widescreen format (16:9)' },
  { label: 'Classic', value: 4/3, icon: '📷', description: 'Traditional photo ratio (4:3)' },
  { label: 'Story', value: 9/16, icon: '📱', description: 'Instagram/FB Stories (9:16)' },
  { label: 'Pinterest', value: 2/3, icon: '📌', description: 'Pinterest optimal (2:3)' },
  { label: 'Banner', value: 21/9, icon: '🎯', description: 'Website headers (21:9)' },
];

function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number,
) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  );
}

export function CropInterface({ image, onCrop, onBack, initialAspectRatio }: CropInterfaceProps) {
  const [imgSrc, setImgSrc] = useState('');
  const imgRef = useRef<HTMLImageElement>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  
  // Find matching initial ratio or default to option 0
  const initialOption = initialAspectRatio
    ? aspectRatioOptions.find((opt) => Math.abs(opt.value - initialAspectRatio) < 0.02) || {
        label: 'Preset Ratio',
        value: initialAspectRatio,
        icon: '📐',
        description: 'Selected preset ratio',
      }
    : aspectRatioOptions[0];

  const [selectedRatio, setSelectedRatio] = useState<AspectRatioOption>(initialOption);

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    const ratioToUse = initialAspectRatio || selectedRatio.value;
    if (ratioToUse) {
      setCrop(centerAspectCrop(width, height, ratioToUse));
    }
  }, [initialAspectRatio, selectedRatio.value]);

  // Load image
  React.useEffect(() => {
    const reader = new FileReader();
    reader.addEventListener('load', () => 
      setImgSrc(reader.result?.toString() || ''));
    reader.readAsDataURL(image);
  }, [image]);

  const handleAspectRatioChange = useCallback((option: AspectRatioOption) => {
    setSelectedRatio(option);
    if (!imgRef.current) return;

    const { width, height } = imgRef.current;
    if (option.value) {
      setCrop(centerAspectCrop(width, height, option.value));
    } else {
      // Free aspect ratio
      setCrop(undefined);
    }
  }, []);

  const handleCompleteCrop = useCallback(async () => {
    if (!imgRef.current || !completedCrop) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
    const scaleY = imgRef.current.naturalHeight / imgRef.current.height;

    const cropWidth = Math.round(completedCrop.width * scaleX);
    const cropHeight = Math.round(completedCrop.height * scaleY);

    canvas.width = cropWidth;
    canvas.height = cropHeight;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      imgRef.current,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      cropWidth,
      cropHeight,
    );

    const base64Image = canvas.toDataURL('image/png');
    onCrop(base64Image);
  }, [completedCrop, onCrop]);

  if (!imgSrc) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <button
          onClick={onBack}
          className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
        >
          ← Back
        </button>
        <div className="flex-1 max-w-xl">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Aspect Ratio</h3>
            <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
              {aspectRatioOptions.map((option) => (
                <button
                  key={option.label}
                  onClick={() => handleAspectRatioChange(option)}
                  className={`p-2 rounded-lg text-sm transition-all ${
                    selectedRatio.label === option.label
                      ? 'bg-blue-100 text-blue-700 ring-2 ring-blue-500'
                      : 'bg-white hover:bg-gray-50 text-gray-700'
                  }`}
                  title={option.description}
                >
                  <span className="flex items-center justify-center gap-1">
                    {option.icon} {option.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
        <button
          onClick={handleCompleteCrop}
          disabled={!completedCrop?.width || !completedCrop?.height}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Apply Crop
        </button>
      </div>

      <div className="border rounded-lg overflow-hidden bg-gray-50 shadow-inner">
        <ReactCrop
          crop={crop}
          onChange={(_, percentCrop) => setCrop(percentCrop)}
          onComplete={(c) => setCompletedCrop(c)}
          aspect={selectedRatio.value || undefined}
          className="max-h-[70vh]"
        >
          <img
            ref={imgRef}
            alt="Crop me"
            src={imgSrc}
            onLoad={onImageLoad}
            className="max-w-full h-auto"
            style={{ maxHeight: '70vh' }}
          />
        </ReactCrop>
      </div>

      <div className="text-sm text-gray-500 text-center space-y-2">
        <p>Drag to move • Handles to resize • Double-click to reset</p>
        {selectedRatio.description && (
          <p className="text-blue-600">{selectedRatio.description}</p>
        )}
      </div>
    </div>
  );
}